import * as React from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

type SearchFieldProps = Omit<React.ComponentProps<'input'>, 'type'> & {
  /**
   * Invoked when the trailing clear button is pressed. When provided in a
   * controlled setup it is the only way the value is reset (the caller owns the
   * state); in an uncontrolled setup it fires *after* the internal value clears.
   */
  onClear?: () => void;
};

function SearchField({
  className,
  value,
  defaultValue,
  onChange,
  onClear,
  disabled,
  // Search/filter fields never want browser autocomplete (past searches,
  // address/identity fills): picking a suggestion also paints Chrome's pale
  // blue autofill surface over the pill. Callers can still opt back in.
  autoComplete = 'off',
  ...props
}: SearchFieldProps) {
  const isControlled = value !== undefined;
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Track the current value for uncontrolled usage so the clear button knows
  // whether to render. In controlled mode the prop is the source of truth.
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');
  const currentValue = isControlled ? value : internalValue;
  const hasValue = String(currentValue ?? '').length > 0;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  }

  function handleClear() {
    if (!isControlled) {
      setInternalValue('');
    }
    onClear?.();
    // Return focus to the field so keyboard users keep their place.
    inputRef.current?.focus();
  }

  return (
    <div
      data-slot="search-field"
      className={cn(
        'relative h-10 w-full rounded-full border border-input-border bg-transparent shadow-field transition-[border-color,color,box-shadow] focus-within:border-input-border-focus focus-within:ring-[3px] focus-within:ring-ring/50',
        'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:border-form-control-border-disabled has-[input:disabled]:bg-form-control-bg-disabled',
        className,
      )}
    >
      <SearchIcon
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-4 z-10 size-5 -translate-y-1/2 text-muted-foreground"
      />
      <input
        ref={inputRef}
        type="search"
        data-slot="search-field-input"
        value={currentValue}
        onChange={handleChange}
        disabled={disabled}
        autoComplete={autoComplete}
        className={cn(
          'h-full w-full min-w-0 appearance-none rounded-full border-0 bg-transparent px-4 py-2 pl-11 text-sm font-normal tracking-[-0.28px] text-input-fg outline-none selection:bg-primary selection:text-primary-foreground placeholder:text-sm placeholder:font-normal placeholder:tracking-[-0.28px] placeholder:text-input-placeholder disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-form-control-fg-disabled disabled:placeholder:text-form-control-fg-disabled [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
          hasValue && 'pr-11',
        )}
        {...props}
      />
      {hasValue && !disabled ? (
        <button
          type="button"
          data-slot="search-field-clear"
          aria-label="Clear search"
          onClick={handleClear}
          className={cn(
            'absolute top-1/2 right-4 z-10 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors outline-none',
            'hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
          )}
        >
          <XIcon className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export { SearchField };
export type { SearchFieldProps };
